import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import isEmail from 'validator/lib/isEmail';
/**
 * Custom validator class for Angular Reactive Form controls
 *
 * To be used in conjunction with <control-messages [control]="form.controlName"></control-messages> to display errors dynamically
 */
export class ValidatorService {
  static getValidatorErrorMessage(validatorName: string, validatorValue?) {
    const config = {
      required: 'This field is required',
      minlength: `Minimum length ${validatorValue.requiredLength}`,
      invalidPhoneNumber: 'Not a valid phone number',
      invalidSSN: 'Not a valid SSN',
      invalidZip: 'Not a valid zip code',
      invalidGovtId: 'Not a valid ID number',
      invalidPercent: 'Not a valid percentage',
      invalidDate: 'Please enter a valid date mm/dd/yyyy',
      futureDate: 'Future date',
      pastDate: 'Please provide a future date',
      unsupportedZip: 'This zip code is not supported.',
      invalidEmail: 'Please enter a valid email',
    };

    return config[validatorName];
  }

  static pastDateValidator(control: FormControl): { [key: string]: boolean } | null {
    const date = new Date(control.value);

    if (control.value === '') {
      return null;
    }

    if (control.value && typeof control.value === 'string') {
      const match = control.value.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
      if (!match) {
        return { invalidDate: true };
      }
      const date = new Date(`${match[3]}-${match[1]}-${match[2]}`);
      if (isNaN(date.getTime())) {
        return { invalidDate: true };
      }
    }

    const year = date.getFullYear().toString();
    if (Number.parseFloat(year) < 1900) {
      return { invalidDate: true };
    }

    if (year.length > 4) {
      return { futureDate: true };
    }

    const today = new Date();
    if (date > today) {
      return { futureDate: true };
    }

    return null;
  }

  static futureDateValidator(control: FormControl): { [key: string]: boolean } | null {
    const date = new Date(control.value);

    if (control.value === '') {
      return null;
    }

    if (control.value && typeof control.value === 'string') {
      const match = control.value.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
      if (!match) {
        return { invalidDate: true };
      }
      const date = new Date(`${match[3]}-${match[1]}-${match[2]}`);
      if (isNaN(date.getTime())) {
        return { invalidDate: true };
      }
    }

    const today = new Date();
    if (date < today) {
      return { pastDate: true };
    }

    return null;
  }

  static phoneValidator(control: FormControl): { [key: string]: boolean } | null {
    // Optional value
    if (!control.value || control.value === '') {
      return null;
    }

    // Must be all digits and at least 10 numbers
    const str = control.value.replace(/[\s-x.+()]/g, '');

    if (!str || str.length < 10 || str.match(/\D/)) {
      return { invalidPhoneNumber: true };
    }

    // If there is an extension - cap extension to 10
    if (str.length > 10) {
      const ext = str.length > 10 ? ' ' + str.substr(10) : '';
      if (ext.length > 11) {
        return { invalidPhoneNumber: true };
      }
    }

    // All good
    return null;
  }

  static percentValidator(control: FormControl): { [key: string]: boolean } | null {
    // RFC 2822 compliant regex
    if (control.value.match(/^[1-9][0-9]?$|^100$/)) {
      return null;
    } else {
      return { invalidPercent: true };
    }
  }

  static ssnValidator(control: FormControl): { [key: string]: boolean } | null {
    // RFC 2822 compliant regex
    if (!control.value) {
      return null;
    }
    if (control.value.match(/^\d{3}[-. ]?\d{2}[-. ]?\d{4}$/)) {
      return null;
    } else if (control.value === '') {
      return null;
    } else {
      return { invalidSSN: true };
    }
  }

  static govtIdValidator(control: FormControl): { [key: string]: boolean } | null {
    // RFC 2822 compliant regex
    if (!control.value) {
      return null;
    }
    if (control.value.match(/^\d{3}[-. ]?\d{2}[-. ]?\d{4}$/)) {
      return null;
    }
    if (control.value.match(/^\d{2}[-. ]?\d{7}$/)) {
      return null;
    } else {
      return { invalidGovtId: true };
    }
  }

  static zipValidator(control: FormControl): { [key: string]: boolean } | null {
    // RFC 2822 compliant regex
    if (!control.value) {
      return null;
    }
    if (control.value.match(/^\d{5}$/)) {
      return null;
    } else if (control.value === '') {
      return null;
    } else {
      return { unsupportedZip: true };
    }
  }

  static requiredWhenVisible(isVisbile: boolean): ValidatorFn {
    return (control: FormControl): { [key: string]: boolean } | null => {
      return isVisbile ? Validators.required(control) : null;
    };
  }

  static emailValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (ValidatorService.isEmailValid(control.value)) {
      return null;
    }
    return { invalidEmail: true };
  }

  static isEmailValid(email: string): boolean {
    if (email && email.length > 0) {
      return isEmail(email);
    }
    return true;
  }
}
